import { Button, Dropdown, Tag } from "antd";
import { PlusOutlined, EllipsisOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { memo } from "react";
import { useAtom, useSetAtom } from 'jotai';
import {
  landsatMosaicItemsLEFTAtom,
  landsatMosaicItemsRIGHTAtom,
  landsatMosaicLoadingAtom,
} from "./store";
import styled from "styled-components";
import { LandsatMosaic_Item } from "~/common/typescript/apis/RemoteImageAlertAPIs";
import { toast } from 'react-toastify';
import { API_URL_ADMIN } from "~/config/config";

export interface LandsatMosaicItemProps {
  item: LandsatMosaic_Item;
}

const getBoundsFromItem = (item: any): [[number, number], [number, number]] => {
  if (item?.bbox?.coordinates && Array.isArray(item.bbox.coordinates[0])) {
    const coords = item.bbox.coordinates[0];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    coords.forEach((pt: any) => {
      const x = Number(pt[0]);
      const y = Number(pt[1]);
      if (!isNaN(x) && !isNaN(y)) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    });
    if (minX !== Infinity && maxX !== -Infinity) {
      return [[minX, minY], [maxX, maxY]];
    }
  }

  if (Array.isArray(item?.bbox) && item.bbox.length === 4) {
    return [[item.bbox[0], item.bbox[1]], [item.bbox[2], item.bbox[3]]];
  }

  // Fallback to Vietnam bounding box
  return [[102.14, 8.18], [109.46, 23.39]];
};

const LandsatMosaicItem = ({ item }: LandsatMosaicItemProps) => {
  const halfWidth = document.body.clientWidth / 2;
  const [listLeft, setListLeft] = useAtom(landsatMosaicItemsLEFTAtom);
  const [listRight, setListRight] = useAtom(landsatMosaicItemsRIGHTAtom);
  const setLoading = useSetAtom(landsatMosaicLoadingAtom);

  const title = item.title_vi || `Landsat Kỳ ${item.period} / ${item.year}`;
  const dateRange = `${dayjs(item.start_date).format('DD/MM/YYYY')} - ${dayjs(item.end_date).format('DD/MM/YYYY')}`;

  const onClickLeft = () => {
    if (window.compare?.setSlider) {
      window.compare.setSlider(halfWidth);
    }
    const exists = listLeft.some((i) => i.id === item.id);
    if (exists) {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });
      return;
    }

    setLoading({
      isLoading: true,
      text: `Đang tải ảnh ${title} cho bản đồ trái...`,
    });

    try {
      setListLeft((prev) => [...prev, item]);
      const bounds = getBoundsFromItem(item);
      const targetMap = window.beforeMap || window.map;
      if (bounds && targetMap) {
        targetMap.fitBounds(bounds, { padding: 40 });
      }

      toast.success('Đã thêm ảnh vào bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });
    } finally {
      setLoading({ isLoading: false, text: '' });
    }
  };

  const onClickRight = () => {
    const exists = listRight.some((i) => i.id === item.id);
    if (exists) {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });
      return;
    }

    setLoading({
      isLoading: true,
      text: `Đang tải ảnh ${title} cho bản đồ phải...`,
    });

    try {
      setListRight((prev) => [...prev, item]);
      const bounds = getBoundsFromItem(item);
      const targetMap = window.map || window.beforeMap;
      if (bounds && targetMap) {
        targetMap.fitBounds(bounds, { padding: 40 });
      }

      toast.success('Đã thêm ảnh vào bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });
    } finally {
      setLoading({ isLoading: false, text: '' });
    }
  };

  const getThumbnailSrc = () => {
    let url = item.thumbUrl;
    if (!url) return '';
    if (url.startsWith('/')) {
      return `${API_URL_ADMIN}${url}`;
    }
    return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
  };

  const menuItems = [
    {
      key: 'add-right',
      label: 'Thêm vào bản đồ phải',
      icon: <PlusOutlined />,
      onClick: onClickRight,
    },
    {
      key: 'add-left',
      label: 'Thêm vào bản đồ trái (để so sánh)',
      icon: <PlusOutlined />,
      onClick: onClickLeft,
    },
  ];

  return (
    <UI_Wrapper>
      <UI_Header>
        <UI_TitleContainer>
          <UI_Name title={title}>{title}</UI_Name>
          <UI_Sub>{dateRange}</UI_Sub>
        </UI_TitleContainer>
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button
            type="text"
            icon={<EllipsisOutlined />}
            size="small"
            onClick={(e) => e.preventDefault()}
          />
        </Dropdown>
      </UI_Header>

      <UI_ImageContainer onClick={onClickRight} title="Nhấp để thêm vào bản đồ phải">
        <img src={getThumbnailSrc()} alt={title} loading="lazy" />
        <UI_TagOverlay>
          <Tag color="#1677ff" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>CDSE</Tag>
          <Tag color="#fa8c16" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>Landsat</Tag>
          <Tag color="#722ed1" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>30m</Tag>
        </UI_TagOverlay>
      </UI_ImageContainer>

      <UI_Footer>
        <Button size="small" type="primary" ghost onClick={onClickLeft}>
          + Trái
        </Button>
        <Button size="small" type="primary" onClick={onClickRight}>
          + Phải
        </Button>
      </UI_Footer>
    </UI_Wrapper>
  );
};

export default memo(LandsatMosaicItem);

const UI_Wrapper = styled.div`
  width: 250px;
  height: 270px;
  border-radius: 8px;
  border: 1px solid #eaeaea;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #1677ff;
    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.12);
  }
`;

const UI_Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const UI_TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  margin-right: 6px;
`;

const UI_Name = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f1f1f;
`;

const UI_Sub = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
`;

const UI_ImageContainer = styled.div`
  position: relative;
  flex: 1;
  margin: 4px 0;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f2f5;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }
`;

const UI_TagOverlay = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  gap: 4px;
`;

const UI_Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;
